/**
 * Fallback price derivation for deals that carry no flat headline price.
 *
 * ~8% of deals have `pricing.original_price`/`pricing.discounted_price` both
 * null while still holding real money in one of the per-vertical grids the AI
 * extractor fills. This module reads those, and is only ever consulted *after*
 * the flat fields come up empty — deals with a normal headline price keep
 * their existing rendering byte-for-byte.
 *
 * Backend field shapes (backend/app/modules/deals/typed/schemas/*.py):
 *   pricing_tiers           every collection — [{tier_label, min_pax, max_pax,
 *                           original_price, discounted_price}]
 *   by_room_type            stay            — [{SGL, TWN, TPL, CVB, CNB}]
 *   by_cabin_type           cruise          — [{cabin_type, single, double, triple, child}]
 *   by_pax_type             experience/service — {label: price}
 *   base_per_room_per_night stay
 *   base_fare               transfer
 *   base_fee                service
 *   private_group_total     experience
 */

type PricingLike = Record<string, unknown> | null | undefined;

export interface DealExtraLike {
  name?: string;
  value?: unknown;
}

export type PriceFallback =
  /** A real number we can format as currency. */
  | { kind: "amount"; label: string; amount: number }
  /** Unstructured seller text from `extras` — shown as-is, never reformatted. */
  | { kind: "text"; label: string; text: string };

function posNum(v: unknown): number | null {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Grid-row keys that hold counts, labels or percentages rather than money.
 * Matched on whole `_`-separated tokens, so `discounted_price` is NOT excluded
 * by the `count` token hiding inside "dis-count-ed".
 */
const NON_PRICE_KEY =
  /(?:^|_)(?:id|pax|nights?|days?|count|qty|quantity|age|percent|percentage|label|name|type|title|slug|code|unit|basis|currency|desc|description)(?:$|_)/i;

function minOf(a: number | null, b: number | null): number | null {
  if (a == null) return b;
  if (b == null) return a;
  return Math.min(a, b);
}

/**
 * Lowest positive money value anywhere inside a price grid / row / scalar.
 * `ignoreKeys` is for `by_pax_type`, a {arbitrary label -> price} dict whose
 * keys are free text ("adult", "2_pax") and whose values are always money —
 * key-filtering there would drop real prices.
 */
function lowestIn(node: unknown, ignoreKeys = false): number | null {
  if (node == null) return null;
  if (typeof node === "number" || typeof node === "string") return posNum(node);
  if (Array.isArray(node)) return node.reduce<number | null>((lo, row) => minOf(lo, lowestIn(row, ignoreKeys)), null);
  if (typeof node === "object") {
    let lo: number | null = null;
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      if (!ignoreKeys && NON_PRICE_KEY.test(k)) continue;
      lo = minOf(lo, lowestIn(v, ignoreKeys));
    }
    return lo;
  }
  return null;
}

/** [pricing key, whether its keys are free-text labels rather than field names] */
const GRID_KEYS: Array<[string, boolean]> = [
  ["pricing_tiers", false],
  ["by_room_type", false],
  ["by_cabin_type", false],
  ["by_pax_type", true],
];

/** Single-value price fields, in the order they should be preferred. */
const BASE_KEYS: Array<[string, string]> = [
  ["base_per_room_per_night", "Per night from"],
  ["base_fare", "Fare from"],
  ["base_fee", "Fee from"],
  ["private_group_total", "Private group from"],
];

const PRICE_EXTRA_NAME = /price|rate|fare|cost|commission|fee/i;

/**
 * What to show when the flat price fields are empty. Callers must check the
 * flat fields themselves first — this deliberately ignores them.
 */
export function derivePriceFallback(pricing: PricingLike, extras?: DealExtraLike[] | null): PriceFallback | null {
  if (pricing) {
    let lowest: number | null = null;
    for (const [k, freeTextKeys] of GRID_KEYS) lowest = minOf(lowest, lowestIn(pricing[k], freeTextKeys));
    if (lowest != null) return { kind: "amount", label: "Starting from", amount: lowest };
    for (const [k, label] of BASE_KEYS) {
      const v = posNum(pricing[k]);
      if (v != null) return { kind: "amount", label, amount: v };
    }
  }
  for (const e of extras ?? []) {
    const name = typeof e?.name === "string" ? e.name : "";
    if (!name || !PRICE_EXTRA_NAME.test(name)) continue;
    const text = e?.value == null ? "" : String(e.value).trim();
    if (!text) continue;
    return { kind: "text", label: name, text };
  }
  return null;
}

/**
 * True when the deal has a real number to show — a flat headline price or any
 * of the alternate grids/base fields above. An `extras` string alone does NOT
 * count as a price (it's unstructured seller prose).
 */
export function hasStructuredPrice(
  pricing: PricingLike,
  flat?: { original_price?: number | null; discounted_price?: number | null } | null,
): boolean {
  if (
    posNum(pricing?.discounted_price) != null ||
    posNum(pricing?.original_price) != null ||
    posNum(flat?.discounted_price) != null ||
    posNum(flat?.original_price) != null
  ) {
    return true;
  }
  return derivePriceFallback(pricing, null) != null;
}
